import type {Cell} from 'notebook/tables/constants/CellConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {selectionStraddlesFreezeBoundary} from 'notebook/tables/core-ui/FreezeColumnUtils';
import {computeHasTextInMergedRegion} from 'notebook/tables/store/logic/TableMergedCellLogic';

/**
 * The result of validating a merge of the current selection.
 */
export interface MergeValidation {
  /**
   * Whether the selection spans across the freeze boundary. Merging is not
   * allowed in this case; the caller should surface the corresponding warning.
   */
  straddlesFreezeBoundary: boolean;

  /**
   * Whether the merge would discard cell values (any cell other than the
   * top-leftmost cell has non-empty text). The caller should confirm with the
   * user before proceeding.
   */
  requiresConfirmation: boolean;
}

/**
 * Validates whether the given (already merge-expanded) selection box can be
 * merged into a single cell.
 *
 * This mirrors the validation previously performed inline in
 * `TableEditable.mergeSelection()`: it does not perform the merge itself, it
 * only reports whether the selection straddles the freeze boundary (in which
 * case merging is disallowed) and whether merging would lose data (in which
 * case the caller should confirm with the user).
 *
 * @param table - The raw table cells.
 * @param box - The merge-expanded selection box.
 * @param freezeBoundary - The absolute freeze boundary column index.
 */
export function computeMergeValidation(
  table: Array<Array<Cell>>,
  box: MultiSelectionBox,
  freezeBoundary: number
): MergeValidation {
  return {
    straddlesFreezeBoundary: selectionStraddlesFreezeBoundary(box.minJ, box.maxJ, freezeBoundary),
    requiresConfirmation: computeHasTextInMergedRegion(table, box),
  };
}

/**
 * Computes whether the table supports merging cells.
 *
 * This mirrors the original `TableEditable.supportsMerge()`, which checked that
 * both `tableMutations.mergeRegion` and `tableMutations.unmergeRegion` were
 * non-null. Those mutation methods are gated solely by the `disableMergingCells`
 * prop (`disableMergingCells ? null : tableOps.mergeTableRegion`), and the
 * underlying `tableOps` merge/unmerge functions are always defined. Merge is
 * therefore supported exactly when merging is not disabled.
 *
 * @param disableMergingCells - Whether merging cells is disabled for this table
 */
export function computeSupportsMerge(disableMergingCells: boolean): boolean {
  return !disableMergingCells;
}
